/**
 * 工作流列表 / 分类接口响应解析与卡片字段规范化（与 WorkflowStorePage 共用）
 */
import { pickCoverFromWorkflowFiles, firstWorkflowFileHoverMedia } from '@/utils/workflowMedia'

/** 首个 Tab：请求列表时不传 flowMark / flowName */
export const SVIP_FREE_TAB_MARK = 'SVIP免费'

export function createSvipFreeTab() {
  return { id: 'tab-svip-free', markName: SVIP_FREE_TAB_MARK, flowMark: '' }
}

/** 业务是否成功（兼容 code / 无 code 仅 data） */
export function isBizSuccess(res) {
  if (res == null) return false
  const c = res.code
  if (c === 0 || c === '0' || c === 200 || c === '200') return true
  if (res.success === true) return true
  if (c == null && res.data !== undefined && !res.msg && !res.message) return true
  return false
}

/** 从任意对象中尽量取出数组列表 */
function pickArray(obj) {
  if (!obj || typeof obj !== 'object') return null
  const keys = [
    'dataList', 'list', 'records', 'rows', 'pageList', 'items', 'content',
    'result', 'flowList', 'workflows', 'flowRecords', 'datas', 'children',
  ]
  for (const k of keys) {
    const v = obj[k]
    if (Array.isArray(v)) return v
  }
  const page = obj.page || obj.pageData || obj.pageInfo
  if (page && typeof page === 'object') {
    for (const k of ['records', 'list', 'rows']) {
      if (Array.isArray(page[k])) return page[k]
    }
  }
  const nested = obj.data
  if (nested && nested !== obj && typeof nested === 'object' && !Array.isArray(nested)) {
    const inner = pickArray(nested)
    if (inner) return inner
  }
  return null
}

/** 从接口响应中取出工作流列表数组（兼容根级 dataList 与 res.data） */
export function extractWorkflowRows(res) {
  if (res == null) return []
  if (Array.isArray(res)) return res
  const rootList = pickArray(res)
  if (rootList) return rootList
  const d = res.data
  if (d == null) return []
  if (Array.isArray(d)) return d
  const picked = pickArray(d)
  return picked || []
}

/** 从接口响应中取出列表总数（dataCount / total） */
export function extractWorkflowDataCount(res) {
  if (res == null) return 0
  const root = res.dataCount ?? res.total ?? res.count
  if (typeof root === 'number' && Number.isFinite(root)) return root
  const d = res.data
  if (d && typeof d === 'object' && !Array.isArray(d)) {
    const nested = d.dataCount ?? d.total ?? d.count
    if (typeof nested === 'number' && Number.isFinite(nested)) return nested
  }
  return 0
}

/** 从接口响应中取出分类 / 标签列表 */
export function extractWorkTypeRows(res) {
  if (res == null) return []
  let raw = res.data
  if (raw == null) return []
  if (Array.isArray(raw)) return raw
  if (typeof raw === 'object') {
    const keys = ['marks', 'markList', 'list', 'records', 'dataList', 'types', 'workTypeList', 'flowMarks', 'categoryList']
    for (const k of keys) {
      if (Array.isArray(raw[k])) return raw[k]
    }
    const picked = pickArray(raw)
    if (picked) return picked
  }
  return []
}

/** 接口行可能是嵌套对象，展开为原一级字段优先的扁平对象 */
export function unwrapWorkflowRow(row) {
  if (!row || typeof row !== 'object') return row
  const nested =
    row.flowBaseInfo ||
    row.flowInfo ||
    row.workflowInfo ||
    row.workflow ||
    row.flow ||
    row.detail ||
    row.info ||
    row.entity ||
    row.vo
  if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
    return { ...nested, ...row }
  }
  return row
}

function isHttpUrl(s) {
  return typeof s === 'string' && /^https?:\/\//i.test(s.trim())
}

function parseApiIsHot(val) {
  if (val === 1 || val === true || val === '1') return true
  if (typeof val === 'string') {
    const s = val.trim()
    if (s.startsWith('1') || s.includes('火爆') || s.toLowerCase().includes('hot')) return true
  }
  return false
}

function parseMoney(v) {
  if (v == null || v === '') return 0
  const n = Number(String(v).replace(/[^\d.-]/g, ''))
  return Number.isFinite(n) ? n : 0
}

/** 规范化单条工作流为卡片渲染所需结构 */
export function normalizeWorkflow(row) {
  if (!row || typeof row !== 'object') return null
  const id = String(row.id ?? row.flowId ?? row.workflowId ?? row.flowNo ?? '')
  const title = row.flowName ?? row.title ?? row.name ?? '未命名'
  const desc = row.flowDes ?? row.desc ?? row.description ?? row.flowDesc ?? row.intro ?? row.remark ?? ''

  const flowIcon = row.flowIcon
  let image =
    (typeof flowIcon === 'string' && isHttpUrl(flowIcon) ? flowIcon.trim() : '') ||
    row.image ||
    row.coverUrl ||
    row.cover ||
    row.picUrl ||
    row.bannerUrl ||
    row.coverImage ||
    row.imgUrl ||
    ''
  if (!image) {
    image = pickCoverFromWorkflowFiles(row.workflowFiles) || 'https://picsum.photos/seed/wf-api/420/240'
  }

  const category = row.flowMark ?? row.category ?? row.categoryName ?? row.typeName ?? row.markName ?? '工作流'
  let tags = row.tags ?? row.tagList ?? row.labelList
  if (typeof tags === 'string') {
    try {
      tags = JSON.parse(tags)
    } catch {
      tags = tags ? tags.split(/[,，]/).map((t) => t.trim()).filter(Boolean) : []
    }
  }
  if (!Array.isArray(tags)) tags = []
  tags = tags.map((t) => (typeof t === 'string' ? t : t?.name || t?.label || String(t))).filter(Boolean)
  if (flowIcon && typeof flowIcon === 'string' && !isHttpUrl(flowIcon) && tags.length < 4) {
    tags = [...tags, flowIcon].filter(Boolean)
  }
  const isHotFlag = parseApiIsHot(row.isHot)
  if (isHotFlag && !tags.includes('热门')) tags.push('热门')

  const files = Array.isArray(row.workflowFiles) ? row.workflowFiles : []
  const fileCount = files.length
  let workflowCount = Number(row.workflowCount ?? row.nodeCount ?? row.flowNodeCount ?? row.nodeNum ?? 0) || 0
  let workflowCountUnit = '个节点'
  if (fileCount > 0 && !workflowCount) {
    workflowCount = fileCount
    workflowCountUnit = '个素材'
  }

  const downloads = Number(row.downloads ?? row.downloadCount ?? row.downloadNum ?? row.downCount ?? 0) || 0
  const thumbCornerCount = downloads > 0 ? downloads : fileCount
  const devTeam = row.createUser ?? row.devTeam ?? row.teamName ?? row.author ?? row.provider ?? row.createBy ?? '—'
  const originalPrice = Number(row.originalPrice ?? row.original ?? row.originPrice ?? row.oldPrice ?? 0) || 0
  const salePrice = parseMoney(row.costPrice ?? row.salePrice ?? row.price ?? row.curPrice ?? row.nowPrice ?? 0)
  const isSvipFree = Boolean(row.isSvipFree ?? row.svipFree ?? row.vipFree ?? row.sVipFree)
  const demoLink = (() => {
    const u = row.flowUrl ?? row.demoLink ?? row.demoUrl ?? row.videoUrl ?? row.url ?? ''
    return typeof u === 'string' ? u.trim() : ''
  })()

  let accounts = []
  if (Array.isArray(row.workflowRecommands) && row.workflowRecommands.length) {
    accounts = row.workflowRecommands.map((r) => {
      if (r == null || typeof r !== 'object') return { name: '—', fans: '', platform: '对标', url: '' }
      const idx = r.recommandIndex ?? r.recommand_index ?? r.indexUrl ?? ''
      return {
        name: String(r.recommandUser ?? r.recommand_user ?? r.name ?? '—'),
        fans: '',
        platform: '对标',
        url: typeof idx === 'string' ? idx.trim() : String(idx || ''),
      }
    })
  }
  if (!accounts.length) {
    let legacy = row.accounts ?? row.accountList ?? row.platformAccounts
    if (typeof legacy === 'string') {
      try {
        legacy = JSON.parse(legacy)
      } catch {
        legacy = []
      }
    }
    if (!Array.isArray(legacy) || legacy.length === 0) {
      accounts = [{
        name: row.accountName ?? row.account ?? '官方',
        fans: row.fans != null ? String(row.fans) : row.fansText != null ? String(row.fansText) : '—',
        platform: row.platform ?? row.platformName ?? '抖音',
        url: '',
      }]
    } else {
      accounts = legacy.map((a) => {
        if (a == null || typeof a !== 'object') {
          return { name: String(a ?? '—'), fans: '', platform: '抖音', url: '' }
        }
        return {
          name: String(a.name ?? a.accountName ?? a.nickName ?? '—'),
          fans: a.fans != null ? String(a.fans) : String(a.fansText ?? ''),
          platform: String(a.platform ?? a.platformName ?? '抖音'),
          url: String(a.url ?? a.link ?? a.profileUrl ?? ''),
        }
      })
    }
  }

  const createTime = row.createTime ?? row.create_time ?? row.gmtCreate ?? ''
  const thumbHoverMedia = firstWorkflowFileHoverMedia(files)

  return {
    id,
    title,
    desc,
    image,
    category,
    tags,
    isSvipFree,
    workflowCount,
    workflowCountUnit,
    devTeam,
    originalPrice,
    salePrice,
    demoLink,
    downloads,
    thumbCornerCount,
    accounts,
    isHotFlag,
    createTime,
    fileCount,
    thumbHoverMedia,
    workflowFiles: files,
  }
}

/** 规范化分类 Tab */
export function normalizeTabs(raw) {
  if (!raw) return []
  const arr = Array.isArray(raw) ? raw : []
  return arr.map((item, index) => {
    if (typeof item === 'string') {
      const isSvip = item === SVIP_FREE_TAB_MARK
      return { id: `tab-${index}`, markName: item, flowMark: isSvip ? '' : item }
    }
    const id = item.id ?? item.markId ?? item.flowMarkId ?? item.code ?? `tab-${index}`
    const markName =
      item.markName ??
      item.name ??
      item.label ??
      item.title ??
      item.typeName ??
      item.flowMarkName ??
      (typeof item.flowMark === 'string' && item.markName == null ? item.flowMark : null) ??
      `分类${index + 1}`
    const isSvip = markName === SVIP_FREE_TAB_MARK
    const flowMark = isSvip ? '' : String(markName)
    return { id: String(id), markName: String(markName), flowMark }
  })
}

export function rowsToNormalizedWorkflows(rows) {
  return rows
    .map((row, idx) => {
      const unwrapped = unwrapWorkflowRow(row)
      const w = normalizeWorkflow(unwrapped)
      if (w && (!w.id || w.id === '')) w.id = `wf-${idx}`
      return w
    })
    .filter(Boolean)
}

function parseCreateTimeForSort(t) {
  if (t == null || t === '') return 0
  const s = String(t).replace(/-/g, '/')
  const d = new Date(s)
  return Number.isNaN(d.getTime()) ? 0 : d.getTime()
}

/** 首页矩阵：热门优先，其次创建时间倒序，截断前 limit 条 */
export function sortAndLimitHomeWorkflows(list, limit = 8) {
  return [...list]
    .sort((a, b) => {
      const hb = b.isHotFlag ? 1 : 0
      const ha = a.isHotFlag ? 1 : 0
      if (hb !== ha) return hb - ha
      return parseCreateTimeForSort(b.createTime) - parseCreateTimeForSort(a.createTime)
    })
    .slice(0, limit)
}
